import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Share2, RotateCcw, RefreshCw } from 'lucide-react';

export function StatsModal({ 
  open, 
  onOpenChange, 
  streak, 
  totalDailyWins, 
  roundsCompleted, 
  maxRounds,
  countdown,
  mode,
  history,
  onShare,
  onReset,
  isChallenge = false,
  onReplayChallenge
}) {
  const isGameOver = mode === 'chain_ended' || mode === 'daily_complete';
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-gray-200 text-black max-w-sm rounded-lg" data-testid="stats-modal">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-center">
            {isChallenge ? 'Challenge Results' : 'Statistics'}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-5">
          {/* Stats Grid - Only show for daily mode */}
          {!isChallenge && (
            <div className="grid grid-cols-4 gap-2 text-center">
              <div>
                <div className="text-3xl font-bold">{streak}</div>
                <div className="text-[10px] text-gray-500">Current Streak</div>
              </div>
              <div>
                <div className="text-3xl font-bold">{totalDailyWins}</div>
                <div className="text-[10px] text-gray-500">Total Wins</div>
              </div>
              <div>
                <div className="text-3xl font-bold">{roundsCompleted}</div>
                <div className="text-[10px] text-gray-500">Rounds Today</div>
              </div>
              <div>
                <div className="text-3xl font-bold">{maxRounds}</div>
                <div className="text-[10px] text-gray-500">Max Rounds</div>
              </div>
            </div>
          )}
          
          {/* Challenge mode message */}
          {isChallenge && (
            <div className="text-center py-4">
              <p className="text-gray-600">
                {isGameOver 
                  ? (history.length > 0 && history[history.length - 1]?.success 
                      ? '🎉 You solved the challenge!' 
                      : 'Better luck next time!')
                  : 'Keep guessing!'}
              </p>
            </div>
          )}
          
          {/* Session History - Only show for daily mode */}
          {!isChallenge && history.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-bold">Today's Rounds</h3>
              <div className="space-y-1 max-h-28 overflow-y-auto">
                {history.map((entry, i) => (
                  <div 
                    key={i} 
                    className="flex items-center justify-between bg-gray-100 rounded px-3 py-2 text-sm"
                  >
                    <span className="text-gray-600">Round {entry.round}</span>
                    <div className="flex items-center gap-2">
                      {entry.success ? (
                        <span className="text-[#6aaa64] font-bold">{entry.guesses}/6</span>
                      ) : (
                        <span className="text-red-500">X</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Next Game Countdown - Only show for daily mode */}
          {!isChallenge && (
            <div className="text-center py-3 border-t border-gray-200">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Next Wordle</p>
              <div className="text-3xl font-bold" data-testid="countdown">
                {countdown}
              </div>
            </div>
          )}
          
          {/* Actions */}
          <div className="flex gap-3">
            {!isChallenge && (
              <Button
                variant="outline"
                className="flex-1 bg-white border-gray-300 hover:bg-gray-100 text-black rounded"
                onClick={onReset}
                data-testid="reset-button"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Reset
              </Button>
            )}
            
            {isChallenge && isGameOver && onReplayChallenge && (
              <Button
                variant="outline"
                className="flex-1 bg-white border-gray-300 hover:bg-gray-100 text-black rounded"
                onClick={() => {
                  onReplayChallenge();
                  onOpenChange(false);
                }}
                data-testid="play-again-button"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Play Again
              </Button>
            )}
            
            {isGameOver && (
              <Button
                className="flex-1 bg-[#6aaa64] hover:bg-[#5a9a54] text-white rounded"
                onClick={onShare}
                data-testid="share-button"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
